import React from "react";
import Card from "@mui/material/Card";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { useAttachmentIdFromRoute, useGameIdFromRoute } from "../games/WithGameIdFromRoute";
import { GameFiles, useGameFiles, useUpdateGameFile } from "@seabrookstudios/pitch2table";
import { ImageOrPdfViewer } from "../core/ImageOrPdfViewer";
import CardHeader from "@mui/material/CardHeader";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { EditableListItemText } from "../core/EditableListItemText";
import { AssetTypeSelect } from "./AssetTypeSelect";
import Archive from "@mui/icons-material/Archive";
import MoveToInbox from "@mui/icons-material/MoveToInbox";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Delete from "@mui/icons-material/Delete";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Path } from "../Path";
import { GameAssetTypeLabels } from "@seabrookstudios/pitch2table-core";
import ListItemText from "@mui/material/ListItemText";

/**
 * @type {import("@seabrookstudios/pitch2table-core").GameAssetType[]}
 */
const SupportsArchive = ["PNP", "Other", "Source", "Photo", "Image"];

/**
 * @type {import("@seabrookstudios/pitch2table-core").GameAssetType[]}
 */
const SupportsDelete = ["PNP", "Other", "Source", "Photo", "Image"];

/**
 * @type {import("@seabrookstudios/pitch2table-core").GameAssetType[]}
 */
const SupportsEditType = ["PNP", "Other", "Source", "Photo", "Image"];

export const FromRoute = () => {
  const gameId = useGameIdFromRoute();
  const attachmentId = useAttachmentIdFromRoute();
  const { data: files = [] } = useGameFiles(gameId);
  const updateFile = useUpdateGameFile(gameId, attachmentId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  if (!attachmentId) {
    return <EmptyDetailCard text="File not found." />;
  }

  const doc = files.find((doc) => doc.id === attachmentId);

  if (!doc) {
    return <EmptyDetailCard text="File not found." />;
  }

  /**
   * @param {string} name
   */
  const onUpdateName = async (name) => {
    await updateFile.mutateAsync({ name, archived: undefined, deleted: undefined, type: undefined });
    await queryClient.invalidateQueries({ queryKey: GameFiles(gameId) });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").GameAssetType} type
   */
  const onEditType = async (type) => {
    await updateFile.mutateAsync({ type, archived: undefined, deleted: undefined, name: undefined });
    await queryClient.invalidateQueries({ queryKey: GameFiles(gameId) });
  };

  const onArchiveHandler = async () => {
    await updateFile.mutateAsync({ type: undefined, archived: !doc.archived, deleted: undefined, name: undefined });
    await queryClient.invalidateQueries({ queryKey: GameFiles(gameId) });
  };

  const onDeleteHandler = async () => {
    await updateFile.mutateAsync({ type: undefined, archived: undefined, deleted: !doc.deleted, name: undefined });
    await queryClient.invalidateQueries({ queryKey: GameFiles(gameId) });

    navigate(Path.game(gameId).files(), { replace: true });
  };

  const IconArchive = doc.archived ? MoveToInbox : Archive;
  const IconDeleted = doc.deleted ? RestoreFromTrash : Delete;

  return (
    <Card elevation={1} square={false}>
      <CardHeader
        action={
          <Stack direction="row">
            {SupportsArchive.includes(doc.type) && (
              <Tooltip title={doc.archived ? "Restore from archive" : "Archive"}>
                <IconButton onClick={onArchiveHandler}>
                  {updateFile.isPending ? <CircularProgress size={24} /> : <IconArchive />}
                </IconButton>
              </Tooltip>
            )}
            {SupportsDelete.includes(doc.type) && (
              <Tooltip title={doc.deleted ? "Restore from trash" : "Delete"}>
                <IconButton onClick={onDeleteHandler}>
                  {updateFile.isPending ? <CircularProgress size={24} /> : <IconDeleted />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
      />
      <List>
        <ListItem>
          <EditableListItemText
            primary="Name"
            secondary={doc.name}
            onSave={onUpdateName}
            isBusy={updateFile.isPending}
            helperText="A name for this file."
          />
        </ListItem>
        <ListItem>
          {!SupportsEditType.includes(doc.type) ? (
            <ListItemText primary="Type" secondary={GameAssetTypeLabels[doc.type]} />
          ) : (
            <AssetTypeSelect
              value={doc.type === "PlayTestPhoto" ? "Photo" : doc.type}
              onChange={onEditType}
              helperText="How the file is used"
              disabled={doc.type === "PlayTestPhoto"}
            />
          )}
        </ListItem>
      </List>
      <ImageOrPdfViewer document={doc} />
    </Card>
  );
};
